import { cn } from '@liza/ui/utils';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { AbnormalFlagLabel } from '../records/AbnormalFlagLabel';
import { isAbnormalObservation } from '../records/observationDisplayUtils';

export type MeasurementValueProps = {
  /** The already-formatted reading, or the em dash placeholder when there is none. */
  value: string;
  /** The reading `value` was formatted from; absent when nothing has been measured. */
  latest?: WithId<Observation>;
  /**
   * Set the value in the destructive color when the lab flagged it or it falls outside its reference
   * range. Off by default: a care-plan reading is the patient's own measurement against a schedule,
   * with no range to be outside of. The labs view turns it on at every level, so a flag spotted on a
   * collapsed category survives expanding it and opening its charts.
   */
  flagAbnormal?: boolean;
  className?: string;
  testId: string;
};

/**
 * A measurement's headline value, shared by the compact row (`MeasurementResultItem`) and the detail
 * header (`MeasurementDetailItem`) so a flagged reading is styled and labelled identically wherever it
 * is read. Only the type scale differs, which the caller passes as `className`.
 */
export function MeasurementValue({
  value,
  latest,
  flagAbnormal,
  className,
  testId,
}: MeasurementValueProps): React.ReactElement {
  const { t } = useTranslation('records');
  const abnormal = !!flagAbnormal && !!latest && isAbnormalObservation(latest);

  return (
    <span
      className={cn('font-medium', className, abnormal && 'font-semibold text-destructive')}
      aria-label={latest ? undefined : t('noData')}
      data-testid={testId}
      data-abnormal={abnormal ? 'true' : undefined}
    >
      {value}
      {abnormal && <AbnormalFlagLabel />}
    </span>
  );
}
